import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Search, Building2, Plus, Pencil, Loader2 } from 'lucide-react';
import { useServerPagination } from '@/hooks/useServerPagination';
import { ListPagination } from '@/components/ListPagination';
import { format } from 'date-fns';
import { formatBusinessDate } from '@/lib/format';
import { toast } from 'sonner';

/**
 * `partners.address` reaches `integrations/supabase/types.ts` only when the types are regenerated
 * against PRODUCTION, after the backend promote — the hatch `lib/agreements/api.ts:4-7` describes.
 */
const db = supabase as unknown as SupabaseClient;

const ITEMS_PER_PAGE = 10;

const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);

interface PartnerSummary {
  partner_id: number;
  partner_name: string;
  partner_created_at: string;
  total_vehicles: number;
  active_vehicles: number;
  outstanding_payout: number;
  last_payout_at: string | null;
  total_count: number;
}

export default function Partners() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [partnersSearch, setPartnersSearch] = useState('');
  const debouncedPartnersSearch = useDebounce(partnersSearch, 300);

  // Sheet state
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<{ id: number; name: string; created_at: string } | null>(null);
  const [partnerName, setPartnerName] = useState('');
  const [partnerAddress, setPartnerAddress] = useState('');
  /** Which partner's stored address is already in the form, so a late read cannot overwrite typing. */
  const hydratedAddressFor = useRef<number | null>(null);

  // Fetch partners using consolidated RPC
  const { data: partnersData, isLoading: partnersLoading } = useQuery({
    queryKey: ['partner-list-summary', debouncedPartnersSearch, 1],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_partner_list_summary', {
        p_search: debouncedPartnersSearch || null,
        p_page: 1,
        p_page_size: 100, // Get all for now since we have <10 partners
      });
      if (error) throw error;
      return data as PartnerSummary[];
    },
  });

  /**
   * THE ADDRESS IS FETCHED, NOT ASSUMED BLANK. `get_partner_list_summary` does not project
   * `partners.address`, so an edit form that opened with an empty box and then saved would clear
   * a stored address every time somebody corrected a name — and that address is the only copy
   * there is, printed on every Vehicle Schedule the partner signs [D-B48].
   */
  const { data: editingAddress, isError: addressFailed } = useQuery({
    queryKey: ['partner-address', editingPartner?.id],
    queryFn: async () => {
      const { data, error } = await db
        .from('partners')
        .select('address')
        .eq('id', editingPartner!.id)
        .single();
      if (error) throw error;
      return (data?.address ?? '') as string;
    },
    enabled: !!editingPartner,
  });

  useEffect(() => {
    if (!editingPartner || editingAddress === undefined) return;
    if (hydratedAddressFor.current === editingPartner.id) return;
    hydratedAddressFor.current = editingPartner.id;
    setPartnerAddress(editingAddress);
  }, [editingPartner, editingAddress]);

  const totalCount = partnersData?.[0]?.total_count ?? 0;
  const partnersPagination = useServerPagination({ totalCount, itemsPerPage: ITEMS_PER_PAGE });

  // Paginated fetch using the RPC
  const { data: paginatedPartners, isLoading: paginatedLoading } = useQuery({
    queryKey: ['partner-list-summary', debouncedPartnersSearch, partnersPagination.currentPage],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_partner_list_summary', {
        p_search: debouncedPartnersSearch || null,
        p_page: partnersPagination.currentPage,
        p_page_size: ITEMS_PER_PAGE,
      });
      if (error) throw error;
      return data as PartnerSummary[];
    },
  });

  const partners = paginatedPartners ?? partnersData;
  const isLoading = paginatedLoading || partnersLoading;

  // Create partner mutation
  const createMutation = useMutation({
    mutationFn: async ({ name, address }: { name: string; address: string }) => {
      const { data, error } = await db
        .from('partners')
        .insert({ name, address: address.trim() || null })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Partner created successfully');
      queryClient.invalidateQueries({ queryKey: ['partner-list-summary'] });
      closeSheet();
    },
    onError: (error: Error) => {
      toast.error(`Failed to create partner: ${error.message}`);
    },
  });

  // Update partner mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, name, address }: { id: number; name: string; address: string }) => {
      const { data, error } = await db
        .from('partners')
        .update({ name, address: address.trim() || null })
        .eq('id', id)
        .select();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Partner updated successfully');
      queryClient.invalidateQueries({ queryKey: ['partner-list-summary'] });
      closeSheet();
    },
    onError: (error: Error) => {
      toast.error(`Failed to update partner: ${error.message}`);
    },
  });

  const openAddSheet = () => {
    setEditingPartner(null);
    hydratedAddressFor.current = null;
    setPartnerName('');
    setPartnerAddress('');
    setSheetOpen(true);
  };

  const openEditSheet = (partner: PartnerSummary) => {
    setEditingPartner({ id: partner.partner_id, name: partner.partner_name, created_at: partner.partner_created_at });
    hydratedAddressFor.current = null;
    setPartnerName(partner.partner_name);
    setPartnerAddress('');
    setSheetOpen(true);
  };

  const closeSheet = () => {
    setSheetOpen(false);
    setEditingPartner(null);
    hydratedAddressFor.current = null;
    setPartnerName('');
    setPartnerAddress('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = partnerName.trim();
    if (!trimmed) return;

    if (editingPartner) {
      updateMutation.mutate({ id: editingPartner.id, name: trimmed, address: partnerAddress });
    } else {
      createMutation.mutate({ name: trimmed, address: partnerAddress });
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  /**
   * NEVER SAVE OVER AN ADDRESS THIS FORM HAS NOT READ BACK — AND "NOT IN FLIGHT" IS NOT THAT.
   *
   * This asked `isFetching`, which goes false on a FAILED read as well as a landed one: React Query
   * exhausts its three retries, `data` stays undefined, the hydration effect returns early on that
   * exact value, `partnerAddress` is still the empty string `openEditSheet` set — and Save is
   * enabled over a blank box. The next name correction then writes `address: null` over the only
   * copy of an address there is, which is the loss the query's own docblock exists to prevent.
   *
   * THE QUERY'S OWN `data` IS THE EVIDENCE, and it is asked rather than the hydration ref: the ref
   * is written inside an effect whose only re-render is `setPartnerAddress`, and React bails out of
   * that when the stored address is the empty string the box already holds — so a ref read during
   * render would leave Save disabled for ever on exactly those partners. `editingAddress` is
   * `undefined` until this partner's read lands and a string (possibly empty) once it has, and the
   * key carries the partner id, so it resets on every switch.
   */
  const addressHydrated = !editingPartner || editingAddress !== undefined;
  const isSaveBlocked = !partnerName.trim() || isSaving || !addressHydrated;

  useEffect(() => {
    partnersPagination.resetPage();
  }, [debouncedPartnersSearch]);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="h-8 w-8 text-primary" />
            <h1 className="text-4xl font-semibold tracking-tight">Partners</h1>
          </div>
          <p className="text-muted-foreground">Manage business partners.</p>
        </div>
        <Button onClick={openAddSheet}>
          <Plus className="h-4 w-4 mr-2" />
          Add Partner
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search partners..."
              value={partnersSearch}
              onChange={(e) => setPartnersSearch(e.target.value)}
              className="pl-9"
            />
          </div>

        </div>
          <ListPagination
            currentPage={partnersPagination.currentPage}
            totalPages={partnersPagination.totalPages}
            totalItems={partnersPagination.totalItems}
            onPageChange={partnersPagination.goToPage}
          />
      </div>

      <Card className="border-0">
        <CardContent className="p-0">
          {isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : partners && partners.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Vehicles</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead>Outstanding</TableHead>
                  <TableHead>Last Payout</TableHead>
                  <TableHead>Partner Since</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partners.map((partner) => (
                  <TableRow key={partner.partner_id} className="group cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/dashboard/partners/${partner.partner_id}`)}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                            {getInitials(partner.partner_name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{partner.partner_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {partner.total_vehicles}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {partner.active_vehicles}
                    </TableCell>
                    <TableCell>
                      {partner.outstanding_payout > 0 ? (
                        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 font-medium">
                          {formatCurrency(partner.outstanding_payout)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {partner.last_payout_at
                        ? formatBusinessDate(partner.last_payout_at)
                        : '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatBusinessDate(partner.partner_created_at)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => { e.stopPropagation(); openEditSheet(partner); }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              {partnersSearch ? 'No partners match your search' : 'No partners found'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Partner Sheet */}
      <Sheet open={sheetOpen} onOpenChange={(open) => { if (!open) closeSheet(); }}>
        <SheetContent
          side="right"
          hideOverlay
          className="w-[400px] m-4 h-[calc(100vh-32px)] rounded-xl border flex flex-col"
        >
          <SheetHeader>
            <SheetTitle>{editingPartner ? 'Edit Partner' : 'Add Partner'}</SheetTitle>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4 flex-1">
            <div className="space-y-2">
              <Label htmlFor="partner-name">Partner Name</Label>
              <Input
                id="partner-name"
                placeholder="Enter partner name"
                value={partnerName}
                onChange={(e) => setPartnerName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="partner-address">Registered Address</Label>
              <Textarea
                id="partner-address"
                autoComplete="off"
                placeholder="House number, street, town"
                value={partnerAddress}
                onChange={(e) => setPartnerAddress(e.target.value)}
                rows={3}
              />
              {editingPartner && !addressHydrated ? (
                /* THE BOX IS EMPTY BECAUSE THE READ HAS NOT LANDED, AND THAT IS NOT THE SAME
                   STATEMENT AS "THIS PARTNER HAS NO ADDRESS". Save is blocked while this shows. */
                <p className="text-xs text-destructive" role="status">
                  {addressFailed
                    ? 'The stored address could not be loaded, so this form cannot save over it. Close the sheet and reopen it.'
                    : 'Loading the stored address…'}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Printed on the Vehicle Schedule the partner signs.
                </p>
              )}
            </div>

            {editingPartner && (
              <p className="text-xs text-muted-foreground">
                Created {format(new Date(editingPartner.created_at), 'MMM d, yyyy')}
              </p>
            )}

            <div className="mt-auto flex gap-3 pb-4">
              <Button
                type="button"
                variant="outline"
                onClick={closeSheet}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSaveBlocked}
                className="flex-1"
              >
                {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingPartner ? 'Save Changes' : 'Add Partner'}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
